import { datadogRum, datadogLogs } from '@mark43/datadog';
import { getMetricTags } from '@m43/core';
import { getAppName } from '../app-name/app-name';
import { getGlobalProperties } from '../global-properties/global-properties';

/**
 * Initialize Datadog RUM (Real User Monitoring) and Logs.
 * Only initializes when credentials are present; no-ops otherwise.
 */
export async function setupDatadog(opts: { sessionReplayEnabled: boolean }): Promise<void> {
    // Get global properties to retrieve Datadog credentials
    const globalProperties = await getGlobalProperties();
    const appName = getAppName();

    const applicationId = globalProperties.DATADOG_APPLICATION_ID;
    const clientToken = globalProperties.DATADOG_CLIENT_TOKEN;
    const environment = globalProperties.ENVIRONMENT;
    const version = globalProperties.VERSION;

    // Determine service name based on app name
    const service = appName === 'cad' ? 'cad-fe' : 'fr-fe';

    // Check if Datadog is configured (credentials are present)
    const shouldNoop = !applicationId || !clientToken;

    if (shouldNoop) {
        return;
    }

    datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service,
        env: environment || 'unknown',
        version,

        sessionSampleRate: 100,

        sessionReplaySampleRate: opts.sessionReplayEnabled ? 100 : 0,

        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,

        allowedTracingUrls: [
            {
                match: (url: string) => {
                    const isTracedDomain = /mark43\.(com|io|uk|us)/.test(url);
                    return isTracedDomain;
                },
                propagatorTypes: ['datadog', 'tracecontext'],
            },
        ],

        beforeSend: (event) => {
            const metricTags = getMetricTags();
            event.context = {
                ...event.context,
                ...metricTags,
            };
            return true;
        },
    });

    // forwardConsoleLogs and forwardErrorsToLogs are disabled to prevent unsanitized PII from reaching Datadog.
    // All logging should go through monitoring/log.ts which sanitizes via sanitizeMessageWithMask() before sending.
    // https://docs.datadoghq.com/logs/log_collection/javascript/
    // https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-logs.LogsInitConfiguration.html
    datadogLogs.init({
        clientToken,
        site: 'datadoghq.com',
        service,
        env: environment || 'unknown',
        version,
        sessionSampleRate: 100,

        beforeSend: (log) => {
            const metricTags = getMetricTags();
            log.context = metricTags;
            return true;
        },
    });
}
